import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { AddStockGroupSchema, type AddStockGroupInput, type StockGroupDto } from 'shared';
import { toast } from 'sonner';

import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { FormField } from '../components/ui/FormField';
import { useAddStockGroup, useRemoveStockGroup } from '../features/marketConsole/mutations';
import { useStockGroups } from '../features/marketConsole/queries';
import { replayToday } from '../lib/useReplayDate';

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function AddGroupForm({ groups }: { groups: readonly StockGroupDto[] }) {
  const add = useAddStockGroup();
  const form = useForm<AddStockGroupInput>({
    resolver: zodResolver(AddStockGroupSchema),
    defaultValues: { symbol: '', group: '', effectiveFrom: replayToday() },
  });
  const groupNames = [...new Set(groups.map((group) => group.group))].sort();

  const submit = form.handleSubmit((input) => {
    add.mutate(input, {
      onSuccess: (group) => {
        toast.success(`${group.symbol} is now in ${group.group} from ${group.effectiveFrom}.`);
        form.reset({ symbol: '', group: input.group, effectiveFrom: input.effectiveFrom });
      },
      onError: (error) => toast.error(errorText(error, `Could not regroup ${input.symbol}.`)),
    });
  });

  return (
    <form className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:items-end" onSubmit={submit}>
      <FormField
        id="stock-group-symbol"
        label="Ticker"
        placeholder="e.g. MSTR"
        autoCapitalize="characters"
        error={form.formState.errors.symbol?.message}
        {...form.register('symbol')}
      />
      <FormField
        id="stock-group-name"
        label="Group"
        placeholder="e.g. Crypto"
        list="stock-group-names"
        error={form.formState.errors.group?.message}
        {...form.register('group')}
      />
      <datalist id="stock-group-names">
        {groupNames.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      <FormField
        id="stock-group-effective"
        label="Effective from"
        type="date"
        max={replayToday()}
        error={form.formState.errors.effectiveFrom?.message}
        {...form.register('effectiveFrom')}
      />
      <Button type="submit" disabled={add.isPending}>
        Save group
      </Button>
    </form>
  );
}

export function StockGroupSettings() {
  const groups = useStockGroups();
  const remove = useRemoveStockGroup();

  return (
    <main className="min-h-screen bg-bg px-4 py-6 text-text sm:px-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <header className="space-y-2">
          <Link
            to="/settings"
            className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Settings
          </Link>
          <h1 className="text-2xl font-semibold">Stock groups</h1>
          <p className="text-sm text-text-muted">
            Put a stock in your own group, such as Crypto, when the data vendor&apos;s industry
            hides what drives it. The group replaces the industry in Stocks, Screener, Now and the
            Flow industry list from the effective date on; earlier sessions and the stored scans
            keep the vendor industry.
          </p>
        </header>

        {groups.isLoading ? (
          <p className="text-sm text-text-muted">Loading stock groups…</p>
        ) : groups.isError || !groups.data ? (
          <p className="text-sm text-danger">Stock groups could not be loaded.</p>
        ) : (
          <>
            <Card header={<h2 className="text-lg font-semibold">Regroup a stock</h2>}>
              <AddGroupForm groups={groups.data} />
            </Card>
            <Card
              header={
                <div className="flex items-baseline justify-between gap-2">
                  <h2 className="text-lg font-semibold">Your groups</h2>
                  <span className="text-xs text-text-muted">{groups.data.length} entries</span>
                </div>
              }
            >
              {groups.data.length === 0 ? (
                <p className="text-sm text-text-muted">
                  No stock is regrouped. Every list uses the vendor industry.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {groups.data.map((group) => (
                    <li key={group.id} className="flex items-center gap-3 py-2 text-sm">
                      <span className="w-16 font-mono font-semibold">{group.symbol}</span>
                      <span className="min-w-0 flex-1 truncate">{group.group}</span>
                      <span className="font-mono text-xs tabular-nums text-text-muted">
                        from {group.effectiveFrom}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${group.symbol} from ${group.group}`}
                        disabled={remove.isPending}
                        onClick={() =>
                          remove.mutate(group.id, {
                            onError: (error) =>
                              toast.error(errorText(error, `Could not remove ${group.symbol}.`)),
                          })
                        }
                        className="rounded p-1.5 text-text-muted hover:bg-surface-raised hover:text-danger disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}
      </div>
    </main>
  );
}
